import React, { useState, useEffect } from 'react';
import { api, useAuth } from '@/contexts/AuthContext';
import { formatDate, formatINRFull, formatStatus } from '@/lib/utils';
import { Plus, Search, Download, Archive, ChevronDown, ChevronUp, Trash2, Edit2, Upload as UploadIcon, X } from 'lucide-react';
import FileUpload from '@/components/FileUpload';

const CATEGORIES = ['RENT', 'SALARY', 'UTILITIES', 'MAINTENANCE', 'DIRECTOR_TRAVEL', 'SALES_VISIT', 'OFFICE_SUPPLIES', 'INSURANCE', 'MISC'];
const TRAVEL_SUB_TYPES = ['Hotel', 'Food', 'Flight', 'Train', 'Bus', 'Cab', 'Fuel', 'Misc'];

export default function OverheadsPage() {
  const { user } = useAuth();
  const [overheads, setOverheads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const fetchOverheads = () => {
    const params = new URLSearchParams();
    if (filterCat) params.set('category', filterCat);
    if (filterLocation) params.set('location', filterLocation);
    api.get(`/overheads?${params}`).then(r => setOverheads(r.data.overheads || [])).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => { fetchOverheads(); }, [filterCat, filterLocation]);

  const canCreate = ['DIRECTOR', 'ACCOUNTANT', 'HR_ADMIN'].includes(user?.role);
  const canViewBills = ['DIRECTOR', 'ACCOUNTANT', 'HR_ADMIN'].includes(user?.role);

  const deleteOverhead = async (id) => {
    if (!window.confirm('Delete this overhead? It will be moved to Deleted Items.')) return;
    try {
      await api.delete(`/overheads/${id}`);
      fetchOverheads();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to delete'); }
  };

  const downloadBillsZip = async (ohId) => {
    try {
      const res = await api.get(`/overheads/${ohId}/bills-zip`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `overhead_bills_${ohId.slice(0, 8)}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (err) { alert('No bills to download or download failed'); }
  };

  return (
    <div data-testid="overheads-page" className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 font-heading tracking-tight">Overheads</h1>
          <p className="text-sm text-slate-500">{overheads.length} expenses</p>
        </div>
        {canCreate && (
          <button onClick={() => setShowCreate(true)} data-testid="add-overhead-btn"
            className="bg-[#1B5486] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#144066] flex items-center gap-2">
            <Plus size={16} /> Add Overhead
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2 text-sm outline-none" data-testid="filter-overhead-cat">
          <option value="">All Categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{formatStatus(c)}</option>)}
        </select>
        <input type="text" placeholder="Filter by location..." value={filterLocation} onChange={e => setFilterLocation(e.target.value)}
          data-testid="filter-overhead-location"
          className="border border-slate-200 rounded-md px-3 py-2 text-sm outline-none min-w-[180px]" />
      </div>

      {loading ? <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-2 border-[#1B5486] border-t-transparent rounded-full" /></div> : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead><tr className="border-b border-slate-100 bg-slate-50/50">
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Date</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Category</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Description</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Location</th>
              <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Amount</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Payment</th>
              <th className="w-24"></th>
            </tr></thead>
            <tbody>
              {overheads.map(oh => (
                <React.Fragment key={oh.id}>
                  <tr className={`border-b border-slate-50 hover:bg-slate-50 cursor-pointer ${expandedId === oh.id ? 'bg-blue-50/50' : ''}`}
                    onClick={() => setExpandedId(expandedId === oh.id ? null : oh.id)} data-testid={`overhead-row-${oh.id}`}>
                    <td className="py-2.5 px-4">{formatDate(oh.date)}</td>
                    <td className="py-2.5 px-4"><span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{formatStatus(oh.category)}</span></td>
                    <td className="py-2.5 px-4 text-slate-700">{oh.description || oh.trip_purpose || '-'}</td>
                    <td className="py-2.5 px-4 text-slate-500 text-xs">{oh.location || '-'}</td>
                    <td className="py-2.5 px-4 text-right font-mono font-medium">{formatINRFull(oh.amount)}</td>
                    <td className="py-2.5 px-4 text-xs text-slate-500">{formatStatus(oh.payment_mode || 'CASH')}</td>
                    <td className="py-2.5 px-4 text-right">
                      <div className="flex gap-1 justify-end">
                        {canViewBills && oh.sub_items?.length > 0 && (
                          <button onClick={(e) => { e.stopPropagation(); downloadBillsZip(oh.id); }} data-testid={`download-bills-${oh.id}`}
                            className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 flex items-center gap-0.5">
                            <Archive size={11} /> ZIP
                          </button>
                        )}
                        {expandedId === oh.id ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
                      </div>
                    </td>
                  </tr>
                  {expandedId === oh.id && (
                    <tr><td colSpan={7} className="px-4 py-3 bg-slate-50/80" data-testid={`sub-items-${oh.id}`}>
                      {canCreate && (
                        <div className="flex gap-2 mb-2">
                          <button onClick={(e) => { e.stopPropagation(); deleteOverhead(oh.id); }} className="text-xs px-3 py-1.5 bg-red-50 text-red-600 rounded hover:bg-red-100 flex items-center gap-1">
                            <Trash2 size={12} /> Delete
                          </button>
                        </div>
                      )}
                      {oh.sub_items?.length > 0 && (
                        <>
                          <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Travel Sub-items</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {oh.sub_items.map((si, i) => (
                          <div key={i} className="flex items-center justify-between bg-white rounded border border-slate-200 px-3 py-2">
                            <div>
                              <p className="text-xs font-medium text-slate-900">{si.type}</p>
                              <p className="text-[10px] text-slate-400">{si.date ? formatDate(si.date) : '-'} | {si.description || '-'}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs font-mono font-medium">{formatINRFull(si.amount)}</p>
                              {si.bill_url && canViewBills && (
                                <a href={si.bill_url} target="_blank" rel="noopener noreferrer"
                                  className="text-[10px] text-[#1B5486] hover:underline flex items-center gap-0.5">
                                  <Download size={10} /> Bill
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                        </>
                      )}
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          </div>
          {overheads.length === 0 && <div className="text-center py-12 text-slate-400">No overhead expenses found</div>}
        </div>
      )}

      {showCreate && <CreateOverheadModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchOverheads(); }} />}
    </div>
  );
}

function CreateOverheadModal({ onClose, onCreated }) {
  const token = localStorage.getItem('access_token');
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), category: 'MISC', description: '', amount: '', payment_mode: 'BANK_TRANSFER', trip_purpose: '', location: '' });
  const [subItems, setSubItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState([]);

  useEffect(() => { api.get('/projects').then(r => setProjects(r.data.projects || [])); }, []);

  const isTravel = ['DIRECTOR_TRAVEL', 'SALES_VISIT'].includes(form.category);

  const addSubItem = () => {
    setSubItems([...subItems, { type: 'Hotel', amount: 0, date: form.date, description: '', bill_url: '' }]);
  };

  const removeSubItem = (idx) => setSubItems(subItems.filter((_, i) => i !== idx));

  const updateSubItem = (idx, field, val) => {
    const u = [...subItems];
    u[idx][field] = field === 'amount' ? (parseFloat(val) || 0) : val;
    setSubItems(u);
  };

  const totalSubItems = subItems.reduce((s, si) => s + (parseFloat(si.amount) || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, amount: isTravel ? totalSubItems : parseFloat(form.amount) || 0 };
      if (isTravel) payload.sub_items = subItems;
      await api.post('/overheads', payload);
      onCreated();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg border w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="create-overhead-modal">
        <h2 className="text-lg font-semibold text-slate-900 font-heading mb-4">Add Overhead Expense</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Date</label>
              <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="input-field w-full" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Category *</label>
              <select value={form.category} onChange={e => { setForm({...form, category: e.target.value}); if (!['DIRECTOR_TRAVEL', 'SALES_VISIT'].includes(e.target.value)) setSubItems([]); }}
                className="input-field w-full" data-testid="overhead-category-select">
                {CATEGORIES.map(c => <option key={c} value={c}>{formatStatus(c)}</option>)}
              </select></div>
          </div>
          <div><label className="block text-xs font-medium text-slate-700 mb-1">{isTravel ? 'Trip Purpose' : 'Description'}</label>
            <input value={isTravel ? form.trip_purpose : form.description}
              onChange={e => setForm({...form, [isTravel ? 'trip_purpose' : 'description']: e.target.value})}
              className="input-field w-full" /></div>
          <div><label className="block text-xs font-medium text-slate-700 mb-1">Location</label>
            <input value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="e.g. Rudrapur, Delhi"
              className="input-field w-full" data-testid="overhead-location" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Payment Mode</label>
              <select value={form.payment_mode} onChange={e => setForm({...form, payment_mode: e.target.value})} className="input-field w-full">
                {['BANK_TRANSFER', 'CASH', 'UPI', 'CHEQUE'].map(m => <option key={m} value={m}>{formatStatus(m)}</option>)}
              </select></div>
            {!isTravel && (
              <div><label className="block text-xs font-medium text-slate-700 mb-1">Amount *</label>
                <input type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} required className="input-field w-full" data-testid="overhead-amount" /></div>
            )}
          </div>

          {/* OVH-01: Travel sub-items */}
          {isTravel && (
            <div className="space-y-3" data-testid="travel-sub-items-section">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-900">Travel Sub-items</h3>
                <button type="button" onClick={addSubItem} data-testid="add-sub-item-btn"
                  className="text-xs text-[#1B5486] hover:underline flex items-center gap-0.5"><Plus size={12} /> Add Item</button>
              </div>
              {subItems.map((si, i) => (
                <div key={i} className="border border-slate-200 rounded-lg p-3 bg-slate-50 space-y-2" data-testid={`sub-item-${i}`}>
                  <div className="grid grid-cols-4 gap-2">
                    <div><label className="block text-[10px] text-slate-500 mb-0.5">Type</label>
                      <select value={si.type} onChange={e => updateSubItem(i, 'type', e.target.value)} className="input-field text-xs w-full" data-testid={`sub-item-type-${i}`}>
                        {TRAVEL_SUB_TYPES.map(t => <option key={t}>{t}</option>)}
                      </select></div>
                    <div><label className="block text-[10px] text-slate-500 mb-0.5">Amount</label>
                      <input type="number" value={si.amount} onChange={e => updateSubItem(i, 'amount', e.target.value)} className="input-field text-xs w-full" data-testid={`sub-item-amount-${i}`} /></div>
                    <div><label className="block text-[10px] text-slate-500 mb-0.5">Date</label>
                      <input type="date" value={si.date} onChange={e => updateSubItem(i, 'date', e.target.value)} className="input-field text-xs w-full" /></div>
                    <div className="flex items-end">
                      <button type="button" onClick={() => removeSubItem(i)} className="text-red-400 hover:text-red-600 p-1"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className="block text-[10px] text-slate-500 mb-0.5">Description</label>
                      <input value={si.description} onChange={e => updateSubItem(i, 'description', e.target.value)} placeholder="e.g. Hotel Marriott" className="input-field text-xs w-full" /></div>
                    <div><label className="block text-[10px] text-slate-500 mb-0.5">Bill Upload</label>
                      <FileUpload token={token} accept=".jpg,.jpeg,.png,.pdf" label="Upload bill"
                        onUploaded={(url) => updateSubItem(i, 'bill_url', url)} />
                      {si.bill_url && <p className="text-[10px] text-emerald-600 mt-0.5">Bill uploaded</p>}
                    </div>
                  </div>
                </div>
              ))}
              {subItems.length > 0 && (
                <div className="flex justify-end text-sm font-semibold text-[#1B5486]" data-testid="sub-items-total">
                  Total: {formatINRFull(totalSubItems)}
                </div>
              )}
              {subItems.length === 0 && <p className="text-xs text-slate-400 py-4 text-center">Click 'Add Item' to add travel sub-items (Hotel, Food, Flight, etc.)</p>}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-slate-200 py-2 rounded-md text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="save-overhead-btn"
              className="flex-1 bg-[#1B5486] text-white py-2 rounded-md text-sm font-medium hover:bg-[#144066] disabled:opacity-60">
              {saving ? 'Saving...' : 'Save Overhead'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
